import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'
import type { Hit } from '@algolia/client-search'
import Link from 'next/link'

import Typography from '@segment/matcha/components/Typography'

import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import CardsGrid from '@segment/matcha/recipes/CardsGrid'
import IntegrationCardBase, * as IntegrationCard from '@segment/matcha/recipes/IntegrationCard'

import type { FetchedFastGrowingCompanies } from '@segment/api/routes/partners/queries'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

const copy = {
  heading: `Fast-growing companies build on Segment`,
}

const link = {
  text: `View all integrations`,
  url: `/catalog/`,
}

interface FastGrowingCompaniesProps {
  entries?: Array<Hit<FetchedFastGrowingCompanies>>
}
const FastGrowingCompanies: FC<PropsWithChildren<FastGrowingCompaniesProps>> = ({ entries }) => {
  const pageConfig = usePageConfig()

  return (
    <Wrapper>
      <Heading variant="h2">{copy.heading}</Heading>

      <CardsGrid limitItems={false}>
        {entries?.map((integration) => (
          <IntegrationCardBase
            href={getAbsoluteUrl(`/catalog/integrations/${integration.type}${integration.slug}`, pageConfig)}
            key={integration.objectID}
          >
            <IntegrationCard.Row centered={true}>
              <IntegrationCard.LogoWrapper>
                <IntegrationCard.Logo
                  width={integration.logo?.width}
                  height={integration.logo?.height}
                  src={integration.logo?.src ?? ``}
                  alt="integration logo"
                />
              </IntegrationCard.LogoWrapper>
            </IntegrationCard.Row>
            <IntegrationCard.Row>
              <IntegrationCard.Name>{integration.name}</IntegrationCard.Name>
            </IntegrationCard.Row>
          </IntegrationCardBase>
        ))}
      </CardsGrid>

      <Link href={getAbsoluteUrl(link.url, pageConfig)}>
        <ButtonText size="medium" tag="span" iconEnd={ChevronRightIcon}>
          {link.text}
        </ButtonText>
      </Link>
    </Wrapper>
  )
}

const Wrapper = styled.section`
  display: grid;
  justify-items: center;
  row-gap: 56px;
  padding: 40px 54px 80px;
  max-width: 512px;
  margin: auto;

  ${({ theme }) => theme.breakpoints.up(`sm`)} {
    padding: 80px 54px;
  }

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    max-width: 1056px;
  }
`

const Heading = styled(Typography)`
  text-align: center;
  margin: 0;
`

export default FastGrowingCompanies
